/**
 * ScreenshotImage — single rendering primitive for execution and snapshot
 * screenshots across the app.
 *
 * Handles two concerns the raw <img> tag doesn't:
 *
 *   1. Relative paths (/screenshots/...) get the API host prefixed.
 *      Screenshot files are served by athena-api, not athena-web — without
 *      the prefix, the browser fetches from the web origin and gets the
 *      SPA index.html fallback (text/html), which renders as a broken icon.
 *
 *   2. Missing or expired files render an inline "no longer available"
 *      placeholder instead of the browser's broken-image icon. Screenshots
 *      are subject to retention; a stale DB reference shouldn't make the
 *      UI look broken.
 *
 * Use it as a drop-in replacement for <img> wherever a screenshot URL is
 * rendered. Style the host element via className/style — the placeholder
 * inherits both so it occupies the same space the image would have.
 */

import { useEffect, useState, type ImgHTMLAttributes, type SyntheticEvent } from 'react'
import { ImageOff } from 'lucide-react'

const API_URL =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_ATHENA_API_URL ||
  'http://localhost:8002'

type ScreenshotImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src: string | null | undefined
}

export function ScreenshotImage({
  src,
  alt,
  className,
  style,
  onError: callerOnError,
  ...rest
}: ScreenshotImageProps) {
  const [errored, setErrored] = useState(false)

  // Reset errored state when src changes — a component instance reused with
  // a different screenshot (e.g., navigating between snapshots in the same
  // detail panel) shouldn't stay stuck on the placeholder.
  useEffect(() => {
    setErrored(false)
  }, [src])

  if (!src) return null

  const resolvedSrc = src.startsWith('http') ? src : `${API_URL}${src}`

  // Compose onError: call the caller's handler first (e.g., their analytics
  // tracking), then trigger our placeholder fallback. Without this, a
  // caller-supplied onError would silently disable the fallback because
  // the spread {...rest} below would overwrite our handler.
  const handleError = (event: SyntheticEvent<HTMLImageElement>) => {
    callerOnError?.(event)
    setErrored(true)
  }

  if (errored) {
    return (
      <div
        role="img"
        aria-label="Screenshot no longer available"
        className={`${className ?? ''} flex flex-col items-center justify-center gap-1 bg-foreground/[0.04] border border-dashed border-subtle text-subtle-foreground`}
        style={{ ...style, minHeight: 64 }}
      >
        <ImageOff className="w-[18px] h-[18px]" aria-hidden="true" />
        <span className="text-[11px]">Screenshot no longer available</span>
      </div>
    )
  }

  return (
    <img
      {...rest}
      src={resolvedSrc}
      alt={alt}
      className={className}
      style={style}
      onError={handleError}
    />
  )
}
